import { type FC } from 'react';
import { Controller } from 'react-hook-form';
import {
  Box,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
} from '@mui/material';

import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';

import { type EditAuditFormChecklist } from './types';

export type AuditFormChecklistPageRendererProps = {
  form: EditAuditFormChecklist;
  isPending: boolean;
  onSubmit: () => void;
};

export const AuditFormChecklistPageRenderer: FC<
  AuditFormChecklistPageRendererProps
> = ({ form, isPending, onSubmit }) => {
  const { control } = form;
  const disableForm = isPending;

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      <Stack spacing={4} sx={{ mt: 2 }} flex={1}>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <ListItem>
            <ListItemText
              primary="I visited every room"
              secondary="When a room was not accessible, I made reasonable efforts to gain access. If it wasn't possible in the end, I indicated that in the background tab."
            />
            <Box sx={{ flexGrow: 0 }}>
              <Controller
                control={control}
                name="isEveryRoomChecked"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl {...getFormErrorAttributes(fieldState.error)}>
                    <Switch {...field} checked={field.value || false} />
                  </FormControl>
                )}
              />
            </Box>
          </ListItem>
          <ListItem>
            <ListItemText
              primary="I filled out every form"
              secondary="I filled out the background form, and all the inventory forms, as completely as possible, given the specific instructions for each one."
            />
            <Box sx={{ flexGrow: 0 }}>
              <Controller
                control={control}
                name="isEveryFormFilled"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl {...getFormErrorAttributes(fieldState.error)}>
                    <Switch {...field} checked={field.value || false} />
                  </FormControl>
                )}
              />
            </Box>
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Pictures of the roof"
              secondary="If the rooftop was accessible, I took a couple pictures of the roof layout from afar."
            />
            <Box sx={{ flexGrow: 0 }}>
              <Controller
                control={control}
                name="hasRooftopPhotos"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl {...getFormErrorAttributes(fieldState.error)}>
                    <Switch {...field} checked={field.value || false} />
                  </FormControl>
                )}
              />
            </Box>
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Pictures of building sides"
              secondary="I took pictures of each side of the building from afar outside."
            />
            <Box sx={{ flexGrow: 0 }}>
              <Controller
                control={control}
                name="hasBuildingSidePhotos"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl {...getFormErrorAttributes(fieldState.error)}>
                    <Switch {...field} checked={field.value || false} />
                  </FormControl>
                )}
              />
            </Box>
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Pictures of doors and windows"
              secondary="I took pictures of each type of door and window."
            />
            <Box sx={{ flexGrow: 0 }}>
              <Controller
                control={control}
                name="hasDoorsAndWindowsPhotos"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl {...getFormErrorAttributes(fieldState.error)}>
                    <Switch {...field} checked={field.value || false} />
                  </FormControl>
                )}
              />
            </Box>
          </ListItem>
          <ListItem>
            <ListItemText
              primary="BAS"
              secondary="If there was a BAS interface onsite, I took pictures of each screen in the interface, including equipment layouts, floor plans, and schedules, as applicable."
            />
            <Box sx={{ flexGrow: 0 }}>
              <Controller
                control={control}
                name="hasBASInterfacePhotos"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl {...getFormErrorAttributes(fieldState.error)}>
                    <Switch {...field} checked={field.value || false} />
                  </FormControl>
                )}
              />
            </Box>
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Thermal images"
              secondary="I took a minimum of 10 thermal images, ideally of all envelope components"
            />
            <Box sx={{ flexGrow: 0 }}>
              <Controller
                control={control}
                name="hasEnvelopeThermalImages"
                disabled={disableForm}
                render={({ field, fieldState }) => (
                  <FormControl {...getFormErrorAttributes(fieldState.error)}>
                    <Switch {...field} checked={field.value || false} />
                  </FormControl>
                )}
              />
            </Box>
          </ListItem>
        </List>
      </Stack>
    </Box>
  );
};
